import algoliasearch from 'algoliasearch/lite';
import { SearchResponse } from '@algolia/client-search';
import { ExternalSearchCache } from './ExternalSearchCache';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import getExternalSearchQueryParams, {
  appendTenantFilterToQueryParams,
  getExternalSearchQueryParamsAndAppendSource,
  getExternalSearchQueryParamsMarketplaceCount
} from './GetExternalSearchQueryParams';
import dayjs from 'dayjs';
import useSearchObjectsToSmartSendItems from '../../search/useSearchObjectsToSmartSendItems';
import { ExternalSearchDelivered } from '~/apps/smartsend/lib/consts/ListyConsts';
import { SearchObject } from '~/components/search/algolia/Models';
import { useMeApi } from '~/lib/api/me/use-me-api';
import { useCallback } from 'react';
import { useTenant } from '@parcel2go/npm-p2g-app';

export interface SearchObjectResponse {
  items: SmartSendItemApiModel[];
  totalItems: number;
  totalPages: number;
}

const useExternalSearch = () => {
  const { tenant } = useTenant();
  const { searchObjectsToSmartSendItems } = useSearchObjectsToSmartSendItems();
  const { getSearchToken } = useMeApi();

  const getExternalSearchCredentials = useCallback(async () => {
    let creds = ExternalSearchCache.creds[tenant];
    const now = dayjs().unix();
    if (!creds || creds.expires < now) {
      const cfg = await getSearchToken();
      if (cfg && cfg.success && cfg.result) {
        const expiration = dayjs().add(10, 'minutes').unix();
        creds = { ...cfg.result, expires: expiration };
        ExternalSearchCache.creds[tenant] = { ...creds };
      }
    }
    return creds;
  }, [getSearchToken, tenant]);

  const getExternalSearchQueryCount = useCallback(
    async (filtersQuery: string): Promise<number> => {
      const creds = await getExternalSearchCredentials();
      if (creds) {
        const searchClient = algoliasearch(creds.application, creds.token);
        const index = searchClient.initIndex(creds.indexName);
        const res = await index.search<SearchObject>('', {
          filters: appendTenantFilterToQueryParams(filtersQuery, tenant),
          hitsPerPage: 1,
          page: 1
        });
        return res?.nbHits || 0;
      } else {
        return Promise.resolve(0);
      }
    },
    [getExternalSearchCredentials, tenant]
  );

  const getExternalSearch = useCallback(
    async (
      page: number,
      pageSize: number,
      filtersQuery: string,
      query: string
    ): Promise<SearchResponse<SearchObject>> => {
      const creds = await getExternalSearchCredentials();
      if (creds) {
        const searchClient = algoliasearch(creds.application, creds.token);
        const index = searchClient.initIndex(creds.indexName);
        return index.search<SearchObject>(query, {
          filters: appendTenantFilterToQueryParams(filtersQuery, tenant),
          hitsPerPage: pageSize,
          page: page
        });
      } else {
        return Promise.resolve(null);
      }
    },
    [getExternalSearchCredentials, tenant]
  );

  const searchExternalSearchAndPollyFillItems = useCallback(
    async (page: number, pageSize: number, filters: string, query: string): Promise<SearchObjectResponse> =>
      getExternalSearch(page, pageSize, filters, query).then((r) =>
        searchObjectsToSmartSendItems(r.hits, r.nbHits, r.nbPages)
      ),
    [getExternalSearch, searchObjectsToSmartSendItems]
  );

  const getByOrderLineId = useCallback(
    async (id: number): Promise<SearchObject> => getExternalSearch(0, 1, '', String(id)).then((r) => r.hits[0]),
    [getExternalSearch]
  );

  const getExternalSearchQueryCountByListyKey = useCallback(
    async (listyKey: string): Promise<number> =>
      getExternalSearchQueryCount(getExternalSearchQueryParams('DEFAULT', listyKey)),
    [getExternalSearchQueryCount]
  );

  const getExternalSearchQueryCountByPaidSource = useCallback(
    async (sources: string[], filter: string = ExternalSearchDelivered): Promise<number> =>
      getExternalSearchQueryCount(getExternalSearchQueryParamsAndAppendSource('LAST6MONTHS', filter, sources)),
    [getExternalSearchQueryCount]
  );

  const getExternalSearchQueryMarketplaceOrderCount = useCallback(
    async (filter: string = ExternalSearchDelivered): Promise<number> =>
      getExternalSearchQueryCount(getExternalSearchQueryParamsMarketplaceCount('LAST6MONTHS', filter)),
    [getExternalSearchQueryCount]
  );

  return {
    searchExternalSearchAndPollyFillItems,
    getExternalSearchQueryCountByListyKey,
    getExternalSearchQueryCount,
    getExternalSearchQueryCountByPaidSource,
    getExternalSearchCredentials,
    getExternalSearchQueryMarketplaceOrderCount,
    getByOrderLineId
  } as const;
};

export default useExternalSearch;
